import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Check, Users } from 'lucide-react';
import type { ContributorGroupToken } from '@sleed/bizcommerce-api';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { SystemGrid, type SystemGridColumn } from '~/components/system/grid/system-grid';
import { serverMessage } from '~/lib/api/server-error';
import { useContributorGroupsList, useContributorGroupMutations } from '~/hooks/use-contributor-groups';
import { ContributorGroupEditDialog } from './group-edit-dialog';

const Bool = ({ on }: { on: boolean }) =>
	on ? <Check size={16} className="text-success-fg" /> : <span className="text-foreground-disabled">—</span>;

/** Contributor groups (Ομάδες Συντελεστών) CRUD surface — parity with the legacy admin grid. */
export function ContributorGroupsGridView() {
	const { t } = useTranslation();
	const { data, isLoading, isError, refetch } = useContributorGroupsList();
	const { remove } = useContributorGroupMutations();

	const [editOpen, setEditOpen] = useState(false);
	const [editing, setEditing] = useState<ContributorGroupToken | null>(null);
	const [deleteRow, setDeleteRow] = useState<ContributorGroupToken | null>(null);

	const columns = useMemo<SystemGridColumn<ContributorGroupToken>[]>(
		() => [
			{
				key: 'name',
				header: t('contributors.groups.columns.name'),
				width: 'minmax(0,1fr)',
				sortValue: o => o.nm ?? '',
				exportValue: o => o.nm ?? '',
				render: o => (
					<span className="truncate text-sm font-medium text-foreground" title={o.nm ?? ''}>
						{o.nm || '—'}
					</span>
				)
			},
			{
				key: 'code',
				header: t('contributors.groups.columns.code'),
				width: 'minmax(0,1fr)',
				sortValue: o => o.cd ?? '',
				exportValue: o => o.cd ?? '',
				render: o => <span className="truncate font-mono text-xs text-foreground-muted">{o.cd || '—'}</span>
			},
			{
				key: 'description',
				header: t('contributors.groups.columns.description'),
				width: 'minmax(0,1.5fr)',
				sortValue: o => o.desc ?? '',
				exportValue: o => o.desc ?? '',
				render: o => (
					<span className="truncate text-sm text-foreground-muted" title={o.desc ?? ''}>
						{o.desc || '—'}
					</span>
				)
			},
			{
				key: 'visible',
				header: t('contributors.groups.columns.visible'),
				width: '90px',
				align: 'center',
				sortValue: o => (o.iv ? 1 : 0),
				exportValue: o => (o.iv ? t('common.yes') : t('common.no')),
				render: o => <Bool on={Boolean(o.iv)} />
			},
			{
				key: 'sort',
				header: t('contributors.groups.columns.sort'),
				width: '80px',
				align: 'right',
				sortValue: o => o.si ?? 0,
				exportValue: o => o.si ?? 0,
				render: o => <span className="text-sm tabular-nums text-foreground-muted">{o.si}</span>
			}
		],
		[t]
	);

	const confirmDelete = () => {
		if (!deleteRow?.id) return;
		remove.mutate(deleteRow.id, {
			onSuccess: () => {
				toast.success(t('common.delete'));
				setDeleteRow(null);
			},
			onError: (err: unknown) => toast.error(serverMessage(err))
		});
	};

	return (
		<>
			<SystemGrid<ContributorGroupToken>
				gridId="contributor-groups"
				title={t('contributors.groups.title')}
				rows={data ?? []}
				loading={isLoading}
				error={isError}
				onRetry={() => void refetch()}
				getRowId={o => o.id ?? o.nm ?? ''}
				columns={columns}
				searchPredicate={(o, q) =>
					(o.nm ?? '').toLowerCase().includes(q) || (o.cd ?? '').toLowerCase().includes(q) || (o.desc ?? '').toLowerCase().includes(q)
				}
				defaultSort={{ key: 'sort', dir: 'asc' }}
				emptyIcon={<Users size={36} className="text-foreground-disabled" strokeWidth={1.25} />}
				addLabel={t('contributors.groups.add')}
				onAdd={() => {
					setEditing(null);
					setEditOpen(true);
				}}
				onEdit={o => {
					setEditing(o);
					setEditOpen(true);
				}}
				onDelete={setDeleteRow}
				exportFilename="contributor-groups"
			/>

			<ContributorGroupEditDialog open={editOpen} onOpenChange={setEditOpen} initial={editing} />

			<Dialog open={Boolean(deleteRow)} onOpenChange={open => !open && setDeleteRow(null)}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t('contributors.groups.deleteTitle')}</DialogTitle>
						<DialogDescription>{t('contributors.groups.deleteBody', { name: deleteRow?.nm || '' })}</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button variant="ghost" onClick={() => setDeleteRow(null)}>
							{t('common.cancel')}
						</Button>
						<Button variant="destructive" onClick={confirmDelete} disabled={remove.isPending}>
							{t('common.delete')}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
}
